import React, { useState, useCallback } from 'react';
import {
  View,
  Image,
  StyleSheet,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { launchImageLibrary, launchCamera, ImagePickerResponse, MediaType } from 'react-native-image-picker';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useRadius } from '@/hooks/shared/useRadius';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CarouselDots } from '@/components/residents/marketplace/CarouselDots';
import { showAlert, showConfirmWithButtons } from '@/utils/common/alert';

const TrashIcon = SVG_COMPONENTS.TRASH;
const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;
const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

export interface MediaUploadItem {
  id: string | number;
  uri: string;
  type: string;
  name?: string;
  size?: number;
  file?: File;
  serverId?: number;
}

// Configuration options
export interface MediaCarouselUploadConfig {
  maxFiles?: number;
  maxFileSizeMB?: number;
  acceptedTypes?: string;
  uploadAreaHeight?: number;
}

interface MediaCarouselUploadProps {
  files: MediaUploadItem[];
  onFilesChange: (files: MediaUploadItem[]) => void;
  config?: MediaCarouselUploadConfig;
  onError?: (message: string) => void;
  variant?: 'light' | 'dark';
  /** Render chevron left/right arrows alongside the carousel dots */
  showChevrons?: boolean;
  /** Custom icon element rendered in the empty upload prompt */
  icon?: React.ReactNode;
  /** Restrict native picker to images only (no video). Default false. */
  imagesOnly?: boolean;
  /** Request Android camera permission before opening camera. Default false. */
  requestCameraPermission?: boolean;
}

// Default configuration
const DEFAULT_CONFIG: Required<MediaCarouselUploadConfig> = {
  maxFiles: 7,
  maxFileSizeMB: 2,
  acceptedTypes: 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime',
  uploadAreaHeight: 384,
};

// Helper functions
const generateFileId = (index: number = 0): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `${timestamp}_${index}_${random}`;
};

const createUploadFileFromWeb = (file: File, index: number): MediaUploadItem => ({
  id: generateFileId(index),
  uri: URL.createObjectURL(file),
  type: file.type || 'image/jpeg',
  name: file.name || `file_${Date.now()}_${index}`,
  size: file.size,
  file: file,
});

const createUploadFileFromAsset = (asset: any, index: number = 0): MediaUploadItem => ({
  id: generateFileId(index),
  uri: asset.uri || '',
  type: asset.type || 'image/jpeg',
  name: asset.fileName || `file_${Date.now()}_${index}`,
  size: asset.fileSize,
});

/**
 * MediaCarouselUpload - Reusable carousel-style media upload component
 * Supports both web and native platforms with image preview and navigation
 */
export const MediaCarouselUpload: React.FC<MediaCarouselUploadProps> = ({
  files,
  onFilesChange,
  config = {},
  onError,
  variant = 'dark',
  showChevrons = false,
  icon,
  imagesOnly = false,
  requestCameraPermission = false,
}) => {
  const { colors: themeColors } = useThemeColors();
  const radius = useRadius();
  const space = useSpacing();
  // The chevrons are bare 20pt glyphs: a padded box pulled back by the same
  // margin gives their row tint a shape without moving the glyph.
  const arrowBox = { padding: space.xs, margin: -space.xs, borderRadius: radius.sm };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Merge config with defaults
  const {
    maxFiles,
    maxFileSizeMB,
    acceptedTypes,
    uploadAreaHeight,
  } = { ...DEFAULT_CONFIG, ...config };

  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
  const hasImages = files.length > 0;

  // Colors based on variant
  const colors = variant === 'dark' ? {
    background: themeColors.card.bg,
    text: themeColors.text.primary,
    textSecondary: themeColors.text.dimmer,
    link: PRIMITIVE_COLORS.resioBlueLight,
    border: themeColors.card.border,
    iconColor: themeColors.text.muted,
    deleteButtonBg: 'rgba(0, 0, 0, 0.6)',
  } : {
    background: '#F5F5F5',
    text: '#1A1A1A',
    textSecondary: '#666666',
    link: '#007AFF',
    border: '#E0E0E0',
    iconColor: '#999999',
    deleteButtonBg: 'rgba(0, 0, 0, 0.55)',
  };

  // Validate file
  const validateFile = useCallback((file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      onError?.(`${file.name} is not a valid image or video file.`);
      return false;
    }
    
    if (file.size > maxFileSizeBytes) {
      onError?.(`${file.name} exceeds ${maxFileSizeMB}MB limit.`);
      return false;
    }
    
    return true;
  }, [maxFileSizeBytes, maxFileSizeMB, onError]);

  // Handle file picker
  const handleFilePick = useCallback(() => {
    const availableSlots = Math.max(maxFiles - files.length, 0);
    
    if (availableSlots === 0) {
      showAlert('Maximum Files', `You can only upload up to ${maxFiles} files.`);
      return;
    }

    if (Platform.OS === 'web') {
      const input = (globalThis as any).document.createElement('input');
      input.type = 'file';
      input.accept = acceptedTypes;
      input.multiple = availableSlots > 1;
      
      input.onchange = () => {
        const selectedFiles = Array.from(input.files || []) as File[];
        const validFiles = selectedFiles.filter(file => validateFile(file));
        
        if (validFiles.length === 0) return;
        
        const trimmed = validFiles.slice(0, availableSlots);
        if (trimmed.length < validFiles.length) {
          showAlert('Too Many Files', `You can only upload ${availableSlots} more file(s).`);
        }
        
        const newFiles = trimmed.map((file, index) => createUploadFileFromWeb(file, index));
        onFilesChange([...files, ...newFiles]);
      };
      
      input.click();
      return;
    }

    // Native platforms
    showConfirmWithButtons(
      'Add Photo or Video',
      'Choose how you want to add media',
      'Photo Library',
      'Camera',
      () => openImageLibrary(availableSlots),
      () => openCamera()
    );
  }, [files, maxFiles, acceptedTypes, validateFile, onFilesChange]);

  const handleImagePickerResponse = useCallback((response: ImagePickerResponse) => {
    if (response.didCancel) return;
    if (response.errorCode) {
      onError?.('Failed to select media. Please try again.');
      return;
    }
    if (response.assets) {
      const availableSlots = Math.max(maxFiles - files.length, 0);
      
      // Filter valid files and check size
      const validAssets = response.assets.filter(asset => {
        if (asset.fileSize && asset.fileSize > maxFileSizeBytes) {
          showAlert('File Too Large', `File exceeds ${maxFileSizeMB}MB limit.`);
          return false;
        }
        return true;
      });
      
      if (validAssets.length === 0) return;
      
      const newFiles = validAssets.slice(0, availableSlots).map((asset, index) => 
        createUploadFileFromAsset(asset, index)
      );
      
      if (newFiles.length < validAssets.length) {
        showAlert('Photo Limit Reached', `Only ${newFiles.length} file(s) added. Maximum is ${maxFiles}.`);
      }
      
      onFilesChange([...files, ...newFiles]);
    }
  }, [files, maxFiles, maxFileSizeBytes, maxFileSizeMB, onFilesChange, onError]);

  const openImageLibrary = useCallback((availableSlots: number) => {
    const options = {
      mediaType: (imagesOnly ? 'photo' : 'mixed') as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
      selectionLimit: availableSlots > 1 ? 0 : 1,
    };
    
    launchImageLibrary(options, handleImagePickerResponse);
  }, [handleImagePickerResponse, imagesOnly]);

  const openCamera = useCallback(async () => {
    if (requestCameraPermission && Platform.OS === 'android') {
      const { PermissionsAndroid } = require('react-native');
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
      );
      if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
        showAlert('Camera Permission Required', 'Please grant camera access in your device settings to take photos.');
        return;
      }
    }

    const options = {
      mediaType: 'photo' as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
    };
    
    launchCamera(options, handleImagePickerResponse);
  }, [handleImagePickerResponse, requestCameraPermission]);

  // Remove file handler
  const handleDeleteImage = useCallback(() => {
    if (files.length === 0) return;
    
    const updatedFiles = files.filter((_, index) => index !== currentImageIndex);
    onFilesChange(updatedFiles);
    
    // Adjust current index if needed
    if (currentImageIndex >= updatedFiles.length && updatedFiles.length > 0) {
      setCurrentImageIndex(updatedFiles.length - 1);
    } else if (updatedFiles.length === 0) {
      setCurrentImageIndex(0);
    }
  }, [files, currentImageIndex, onFilesChange]);

  const canAddMore = files.length < maxFiles;
  const totalDots = hasImages ? (canAddMore ? files.length + 1 : files.length) : 1;
  const isOnUploadSlide = hasImages && canAddMore && currentImageIndex === files.length;
  const showUploadPrompt = !hasImages || isOnUploadSlide;

  const canGoPrev = currentImageIndex > 0;
  const canGoNext = currentImageIndex < totalDots - 1;
  const goToPrev = useCallback(() => { if (canGoPrev) setCurrentImageIndex(i => i - 1); }, [canGoPrev]);
  const goToNext = useCallback(() => { if (canGoNext) setCurrentImageIndex(i => i + 1); }, [canGoNext]);

  const defaultIcon = (
    <View style={styles.iconContainer}>
      <RESIDENTS_SVG_COMPONENTS.MAINTENANCE width={48} height={48} color={colors.iconColor} />
      <View style={styles.plusIconOverlay}>
        <Text style={[styles.plusText, { color: colors.iconColor }]}>+</Text>
      </View>
    </View>
  );

  return (
    <View
      style={[
        styles.container,
        { borderWidth: 1, borderColor: colors.border, borderStyle: showUploadPrompt ? 'dashed' : 'solid' },
      ]}
    >
      {/* Main Upload/Preview Area */}
      <View style={[styles.uploadArea, { backgroundColor: colors.background, height: uploadAreaHeight }]}>
        {showUploadPrompt ? (
          // Empty state / Upload prompt
          <Hoverable
            hover="row"
            bleed={{ x: 0, y: space.sm }}
            style={styles.uploadPrompt}
            onPress={handleFilePick}
          >
            {icon || defaultIcon}
            
            <View style={styles.textContainer}>
              <Text style={[styles.uploadTitle, { color: colors.text }]}>Add photo or video</Text>
              <Hoverable hover="link" onPress={handleFilePick}>
                {({ textStyle }) => (
                  <Text style={[styles.uploadLink, { color: colors.link }, textStyle]}>Click to upload</Text>
                )}
              </Hoverable>
            </View>
            
            <Text style={[styles.uploadHint, { color: colors.textSecondary }]}>
              Max file size {maxFileSizeMB}mb • Max {maxFiles} files
            </Text>
          </Hoverable>
        ) : (
          // Image preview
          <View style={styles.imagePreviewContainer}>
            <Image
              source={{ uri: files[currentImageIndex]?.uri }}
              style={styles.previewImage}
              resizeMode="cover"
            />
            <Hoverable
              hover="button"
              style={[styles.deleteButton, { backgroundColor: colors.deleteButtonBg }]}
              onPress={handleDeleteImage}
            >
              <TrashIcon width={20} height={20} color={PRIMITIVE_COLORS.white} />
            </Hoverable>
          </View>
        )}
      </View>
      
      <View style={[styles.dotsBar, { backgroundColor: colors.background, borderTopColor: colors.border }]}>
        {totalDots > 1 && (
          showChevrons ? (
            <View style={styles.navRow}>
              <Hoverable
                hover="row"
                onPress={goToPrev}
                disabled={!canGoPrev}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                style={[arrowBox, !canGoPrev && styles.arrowDisabled]}
              >
                <ChevronLeftIcon width={20} height={20} color={canGoPrev ? colors.text : colors.textSecondary} />
              </Hoverable>
              <CarouselDots
                count={totalDots}
                activeIndex={currentImageIndex}
                activeColor={colors.text}
                inactiveColor={colors.textSecondary}
                onDotPress={setCurrentImageIndex}
                showBackground={false}
                containerStyle={styles.dotsContainerOverride}
              />
              <Hoverable
                hover="row"
                onPress={goToNext}
                disabled={!canGoNext}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                style={[arrowBox, !canGoNext && styles.arrowDisabled]}
              >
                <ChevronRightIcon width={20} height={20} color={canGoNext ? colors.text : colors.textSecondary} />
              </Hoverable>
            </View>
          ) : (
            <CarouselDots
              count={totalDots}
              activeIndex={currentImageIndex}
              activeColor={colors.text}
              inactiveColor={colors.textSecondary}
              onDotPress={setCurrentImageIndex}
              showBackground={false}
              containerStyle={styles.dotsContainerOverride}
            />
          )
        )}
        {files.length > 0 && (
          <Text style={[styles.fileCount, { color: colors.textSecondary }]}>
            {files.length} / {maxFiles} files
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  uploadArea: {
    width: '100%',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadPrompt: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    gap: 14,
    ...(Platform.OS === 'web' && ({
      cursor: 'pointer',
    } as any)),
  },
  iconContainer: {
    width: 48,
    height: 48,
    position: 'relative',
  },
  plusIconOverlay: {
    position: 'absolute',
    top: 7,
    right: -5,
    width: 13,
    height: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  plusText: {
    fontSize: 16,
    fontWeight: '500',
  },
  textContainer: {
    alignItems: 'center',
    gap: 6,
  },
  uploadTitle: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  uploadLink: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  uploadHint: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
    textAlign: 'center',
  },
  imagePreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsBar: {
    borderTopWidth: 1,
    height: 48,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  navRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  arrowDisabled: {
    opacity: 0.4,
  },
  dotsContainerOverride: {
    position: 'relative',
    bottom: 0,
  },
  fileCount: {
    position: 'absolute',
    right: 16,
    fontSize: 12,
    fontWeight: '400',
  },
});

export default MediaCarouselUpload;
